import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { getCityBySlug } from '../data/cities';

function slugToDisplayName(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const API_BASE = import.meta.env.VITE_API_URL
  ? `https://${import.meta.env.VITE_API_URL}`
  : 'https://api.helpped.com';

interface ArticleSummary {
  slug: string;
  title: string;
  meta_description: string;
  article_type: string;
  published_at: string | null;
  word_count: number | null;
}

function relDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ArticleIndex() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = getCityBySlug(citySlug ?? '');
  const displayName = city?.name ?? slugToDisplayName(citySlug ?? '');

  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [loading, setLoading]   = useState(true);

  usePageMeta(
    `${displayName} City Council News & Decisions | Helpped`,
    `Latest ${displayName}, WA city council votes, decisions, and meeting coverage. Updated daily.`,
    city ? { placename: `${city.name}, Washington`, lat: city.lat, lon: city.lon } : undefined,
  );

  useEffect(() => {
    if (!citySlug) return;
    fetch(`${API_BASE}/api/cities/${citySlug}/council`)
      .then((r) => r.ok ? r.json() : [])
      .then((d) => { setArticles(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [citySlug]);

  if (!citySlug) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}>H</div>
            <span className="font-bold text-gray-900">Helpped</span>
          </Link>
          <span className="text-gray-300 mx-1">/</span>
          {city
            ? <Link to={`/cities/${citySlug}`} className="text-gray-500 hover:text-gray-800">{displayName}</Link>
            : <span className="text-gray-500">{displayName}</span>
          }
          <span className="text-gray-300 mx-1">/</span>
          <span className="text-gray-700 font-medium">Council</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10 w-full flex-1">
        {/* Breadcrumb schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://helpped.com/' },
            { '@type': 'ListItem', position: 2, name: displayName, item: `https://helpped.com/cities/${citySlug}` },
            { '@type': 'ListItem', position: 3, name: 'Council', item: `https://helpped.com/cities/${citySlug}/council` },
          ],
        })}} />

        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">
          {displayName} City Council
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Votes, decisions, and meeting coverage — updated daily.
        </p>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-full mb-1" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <p className="text-3xl mb-3">🏛️</p>
            <p className="text-sm">Council articles are being generated — check back soon.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {articles.map((a) => (
              <article key={a.slug} className="py-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background: a.article_type === 'meeting' ? 'hsl(38 90% 94%)' : 'hsl(158 68% 94%)',
                      color: a.article_type === 'meeting' ? 'hsl(38 60% 35%)' : 'hsl(158 68% 28%)',
                    }}>
                    {a.article_type === 'meeting' ? 'Meeting' : 'Council Vote'}
                  </span>
                  <span className="text-xs text-gray-400">{relDate(a.published_at)}</span>
                </div>
                <Link to={`/cities/${citySlug}/council/${a.slug}`}
                  className="group block">
                  <h2 className="text-base font-semibold text-gray-900 group-hover:text-green-700 transition-colors leading-snug mb-1">
                    {a.title}
                  </h2>
                  <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                    {a.meta_description}
                  </p>
                </Link>
              </article>
            ))}
          </div>
        )}
      </main>

      <footer className="px-6 py-6 border-t border-gray-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-400">
          {city
            ? <Link to={`/cities/${citySlug}`} className="hover:text-gray-600">← Back to {displayName}</Link>
            : <Link to="/" className="hover:text-gray-600">← Helpped.com</Link>
          }
          <Link to="/" className="hover:text-gray-600">Helpped.com</Link>
        </div>
      </footer>
    </div>
  );
}
